"use client";

import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const FAQS = [
  { q: "Who is Dr. Jyoti Kush?", a: "The Chief Operating Officer of CryptoMize and MaxiMize Infinium — the world's only full-spectrum Digital Conglomerate for Strategic Sovereignty. A physician-turned-executive, a women's empowerment advocate, and a thought leader on operational excellence." },
  { q: "What does Dr. Jyoti Kush do?", a: "Oversees the operational execution of complex campaigns, governance programs, and strategic sovereignty operations. The role spans sixty-plus integrated services and teams across three continents." },
  { q: "What is the 70% Project?", a: "The flagship initiative documenting the operating case — backed by a decade of evidence — for building teams that are over seventy percent women, and why this model outperforms. Not a diversity initiative. A talent strategy with verified operational results." },
  { q: "What is The Operator's Notebook?", a: "A biweekly publication offering one short, disciplined note on how excellent things run. No filler. No slogans. One principle, made usable, every two weeks." },
  { q: "What makes the approach unique?", a: "The only operator at sovereign scale whose discipline was forged in clinical practice. The medical method is the actual operating system, not a metaphor. Combined with sixty-plus integrated services, zero third-party dependencies, and a decade of sovereign-scale operations." },
  { q: "How does crisis management work?", a: "Through a crisis transformation engine operating at 384-1416x the speed of traditional approaches. Sub-5-minute response, 85-95% crisis success rate. Crisis is raw material to be shaped into lasting competitive advantage." },
  { q: "What is Strategic Sovereignty?", a: "The integration of intelligence, security, and strategy under a single command — the operating model that MaxiMize Infinium pioneered. The capability to maintain complete operational independence: zero third-party dependencies, sovereign-grade security." },
];

export function FAQSection() {
  const [openIdx, setOpenIdx] = React.useState<number | null>(0);
  return (
    <Section id="faq" ariaLabel="Frequently Asked Questions" background="muted">
      <SectionHeader
        icon={HelpCircle}
        overline="FAQ"
        overlineClassName="text-purple"
        pillClassName="glass-tinted-purple border-purple/20"
        title={
          <>
            Frequently Asked <span className="text-gradient-royal">Questions.</span>
          </>
        }
        subtitle="Direct answers to the most common questions about the operator, the organization, and the approach."
      />
      <div className="max-w-4xl mx-auto space-y-2">
        {FAQS.map((f, i) => {
          const isOpen = openIdx === i;
          return (
            <motion.div
              key={f.q}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="glass-card rounded-xl overflow-hidden border border-border/30"
            >
              <button
                onClick={() => setOpenIdx(isOpen ? null : i)}
                className="w-full flex items-start justify-between gap-4 p-5 text-left hover:bg-red/5 transition-colors"
              >
                <span className="text-body-md font-semibold flex-1">{f.q}</span>
                <ChevronDown className={`size-4 shrink-0 transition-transform ${isOpen ? "rotate-180 text-red" : "text-muted-foreground"}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 text-body-sm text-muted-foreground leading-relaxed border-t border-border/30 pt-4">
                      {f.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}
